// src/pages/PaymentSuccess.jsx
import { useEffect, useState } from "react";
import { useLocation, useSearchParams, Link } from "react-router-dom";
import { apiFetch } from "../lib/apiFetch";
import { useAccess } from "../contexts/AccessContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const [state, setState] = useState({ status: "loading", message: "", orderId: null });
  const location = useLocation();
  const { refreshAccess } = useAccess() || {};

  const defaultReturnPath = "/discover/marketplace";
  const queryReturnTo = params.get("returnTo");
  const locationReturnTo = location.state?.returnTo;
  const returnTo = queryReturnTo || locationReturnTo || defaultReturnPath;

  useEffect(() => {
    const paymentIntentId = params.get("payment_intent");
    const redirectStatus = params.get("redirect_status");

    if (!paymentIntentId) {
      setState({ status: "error", message: "Missing payment_intent in URL. Please try again from the Shop page.", orderId: null });
      return;
    }

    async function finalize() {
      try {
        const res = await apiFetch("/api/shop/payment-success", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paymentIntentId }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || !json?.ok) {
          throw new Error(json?.error || `Finalize failed (${res.status})`);
        }
        setState({ status: "ok", message: "", orderId: json.orderId || null });
        // Refresh access after payment success
        await refreshAccess?.();
      } catch (err) {
        setState({ status: "error", message: err?.message || "Payment verification failed.", orderId: null });
      }
    }

    finalize();
  }, [params]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-black text-white">
      <div className="max-w-md p-6 border border-gray-700 rounded-md">
        {state.status === "loading" && (
          <>
            <h1 className="text-xl font-semibold">Processing payment</h1>
            <p className="mt-2 text-sm text-gray-300">Verifying payment with Mudo servers.</p>
          </>
        )}
        {state.status === "ok" && (
          <>
            <h1 className="text-xl font-semibold">Payment Success</h1>
            <p className="mt-2 text-sm text-gray-300">Your payment was confirmed and funds have been transferred to the studio.</p>
            {state.orderId && (
              <p className="mt-2 text-sm text-gray-400">Order ID: {state.orderId}</p>
            )}
            <div className="mt-4">
              <Link to={returnTo} className="text-blue-400 underline">
                Back to Shop
              </Link>
            </div>
          </>
        )}
        {state.status === "error" && (
          <>
            <h1 className="text-xl font-semibold">Payment Verification Failed</h1>
            <p className="mt-2 text-sm text-red-300">{state.message}</p>
            <div className="mt-4">
              <Link to={returnTo} className="text-blue-400 underline">
                Back to Shop
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
